import { isProjectVersion, type Project } from '@obraplan/core/domain/structure/project';

/**
 * Uma obra aprovada e as versões de orçamento que nasceram dela (07/10/2026).
 *
 * `root` é a aprovada. Quando a aprovada não está na lista — o perfil alcança a versão e
 * não a obra de origem —, a própria versão abre a família: some-la da tela seria esconder
 * dado que a pessoa tem direito de ver.
 */
export interface ProjectFamily {
  readonly root: Project;
  /** Em ordem de número: a versão 2 antes da 3. */
  readonly versions: readonly Project[];
}

export function groupProjectFamilies(projects: readonly Project[]): ProjectFamily[] {
  const byId = new Set(projects.map((project) => project.id as string));
  const versionsOf = new Map<string, Project[]>();
  const roots: Project[] = [];

  for (const project of projects) {
    const approvedId = project.versionOf as string | undefined;
    if (isProjectVersion(project) && approvedId !== undefined && byId.has(approvedId)) {
      versionsOf.set(approvedId, [...(versionsOf.get(approvedId) ?? []), project]);
    } else {
      roots.push(project);
    }
  }

  return roots.map((root) => ({
    root,
    versions: [...(versionsOf.get(root.id as string) ?? [])].sort(
      (a, b) => (a.versionNumber ?? 0) - (b.versionNumber ?? 0),
    ),
  }));
}

/**
 * A obra aprovada da família de `project`, procurada na lista: a própria obra, quando ela
 * é a aprovada; `versionOf` quando é versão. `undefined` quando a aprovada não está na
 * lista.
 */
export function approvedOf(project: Project, projects: readonly Project[]): Project | undefined {
  if (!isProjectVersion(project)) return project;
  return projects.find((candidate) => candidate.id === project.versionOf);
}
